import { Injectable, inject } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { PhotographyApiService } from './api.service';
import { PhotographyListResponse } from '../models/photography-vendor.model';
import { PhotographyPrice, PhotographyProduct } from '../models/photography-catalog.model';

const PRICES = 'prices';

/** Mirrors CreateProductRequest in @lcms-photography/core. Updates send any subset of these. */
export interface PhotographyProductRequest {
  vendorId?: string;
  name?: string;
  description?: string;
  selectedImage?: string;
  unselectedImage?: string;
}

/** Mirrors CreatePriceRequest in @lcms-photography/core. Money values are decimal strings like "150.00". */
export interface PhotographyPriceRequest {
  productId?: string;
  vendorRegionId?: string;
  releaseId?: string;
  cost?: string;
  travelCost?: string;
  bundleCost?: string;
}

/** Products in the lcms-photography catalog and their prices */
@Injectable({
  providedIn: 'root',
})
export class PhotographyProductService {
  private api = inject(PhotographyApiService);

  readonly resource = 'products';

  get$(productId: string): Observable<PhotographyProduct> {
    return this.api.get<PhotographyProduct>(`${this.resource}/${productId}`);
  }

  create(product: PhotographyProductRequest): Promise<PhotographyProduct> {
    return this.api.create<PhotographyProduct, PhotographyProductRequest>(this.resource, product);
  }

  update(productId: string, product: PhotographyProductRequest): Promise<PhotographyProduct> {
    return this.api.update<PhotographyProduct, PhotographyProductRequest>(`${this.resource}/${productId}`, product);
  }

  /** Rejects with a 409 while prices or bundles still reference the product */
  delete(productId: string): Promise<void> {
    return firstValueFrom(this.api.delete<void>(`${this.resource}/${productId}`));
  }

  prices$(productId: string): Observable<PhotographyPrice[]> {
    return this.api.get<PhotographyListResponse<PhotographyPrice>>(PRICES, { productId, limit: 200 }).pipe(
      map((response) => response.items),
    );
  }

  getPrice$(priceId: string): Observable<PhotographyPrice> {
    return this.api.get<PhotographyPrice>(`${PRICES}/${priceId}`);
  }

  createPrice(price: PhotographyPriceRequest): Promise<PhotographyPrice> {
    return this.api.create<PhotographyPrice, PhotographyPriceRequest>(PRICES, price);
  }

  updatePrice(priceId: string, price: PhotographyPriceRequest): Promise<PhotographyPrice> {
    return this.api.update<PhotographyPrice, PhotographyPriceRequest>(`${PRICES}/${priceId}`, price);
  }

  deletePrice(priceId: string): Promise<void> {
    return firstValueFrom(this.api.delete<void>(`${PRICES}/${priceId}`));
  }
}
